import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../supabase";
import { createNotification } from "../utils/notifications";
import { useFocusTrap } from "../utils/focusTrap";
import {
  IoShieldOutline,
  IoFlagOutline,
  IoPeopleOutline,
  IoImagesOutline,
  IoChatbubbleOutline,
  IoChatbubbleEllipsesOutline,
  IoListOutline,
  IoStatsChartOutline,
  IoTrendingUpOutline,
  IoServerOutline,
  IoSettingsOutline,
  IoPersonRemoveOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoClose,
  IoExpandOutline,
  IoRibbonOutline
} from "react-icons/io5";

// One source of truth for the tab bar — icon, label, and (for reports) the
// badge all live here instead of being repeated across the button markup.
const TABS = [
  { key: "reports", label: "Reports", icon: IoFlagOutline },
  { key: "users", label: "Users", icon: IoPeopleOutline },
  { key: "verification", label: "Verify", icon: IoRibbonOutline },
  { key: "designs", label: "Designs", icon: IoImagesOutline },
  { key: "comments", label: "Comments", icon: IoChatbubbleOutline },
  { key: "feedback", label: "Feedback", icon: IoChatbubbleEllipsesOutline },
  { key: "log", label: "Log", icon: IoListOutline },
  { key: "stats", label: "Stats", icon: IoStatsChartOutline },
  { key: "analytics", label: "Analytics", icon: IoTrendingUpOutline },
  { key: "storage", label: "Storage", icon: IoServerOutline },
  { key: "settings", label: "Settings", icon: IoSettingsOutline }
];

// Every write action on this page (ban, promote, verify, delete, warn) goes
// through its own admin-gated SECURITY DEFINER RPC (checks am_i_admin()
// internally) and logs itself into admin_actions — a future addition should
// follow that same shape rather than doing a raw table write from here.
//
// Bootstrapping note: the very first admin can't be created this way, or
// even by a plain UPDATE from the SQL editor — the users table's
// protect_admin_fields trigger (prevent_self_privilege_escalation()) only
// lets an EXISTING admin change is_admin/banned for someone else, and
// auth.uid() is null in a raw SQL session either way. To promote the first
// admin ever (e.g. after a fresh database), run this once:
//   ALTER TABLE users DISABLE TRIGGER protect_admin_fields;
//   UPDATE users SET is_admin = true WHERE username = '...';
//   ALTER TABLE users ENABLE TRIGGER protect_admin_fields;
const PAGE_SIZE = 50;
const USER_PAGE_SIZE = 20;
const DESIGN_PAGE_SIZE = 20;
const COMMENT_PAGE_SIZE = 20;
const FEEDBACK_PAGE_SIZE = 30;
const LOG_PAGE_SIZE = 50;
const VERIFICATION_PAGE_SIZE = 20;

export default function Admin() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null); // null = checking
  const [view, setView] = useState("reports"); // "reports" | "users" | "designs" | "feedback" | "log" | "stats"

  // ----- Reports -----
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  // Sets, not a single id — a plain single-value guard blocked EVERY row's
  // action while ANY one report's action was in flight (e.g. dismissing
  // report A silently no-op'd a ban click on report B, with no feedback at
  // all, since B's own disabled={actioningId.has(r.id)} was false). Keyed per
  // id instead, so only a genuine double-click on the SAME report is
  // guarded against — dismissing or banning twice is harmless (both just
  // re-set the same status/flag), but warnUser isn't: two clicks would send
  // the target two duplicate warning notifications instead of one.
  const [actioningId, setActioningId] = useState(() => new Set());
  const actioningRef = useRef(new Set());
  // Bumped on every filter-driven reset fetch — a fetch whose result comes
  // back after a newer one started (statusFilter flipped again before the
  // first request resolved) checks this and discards itself instead of
  // clobbering the newer, correct list with stale data.
  const reportsRequestRef = useRef(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Checked synchronously, unlike the loadingMore state above — a fast
  // double-tap on "Load more" fires before that state update commits,
  // fetching (and appending) the same page twice.
  const loadingMoreRef = useRef(false);
  // Independent of statusFilter/loadReports — the tab badge always means
  // "pending count", so it needs its own count regardless of which filter
  // the list itself happens to be showing right now.
  const [pendingCount, setPendingCount] = useState(0);

  // ----- Users -----
  const [userSearch, setUserSearch] = useState("");
  const [usersBannedOnly, setUsersBannedOnly] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersOffset, setUsersOffset] = useState(0);
  const [usersHasMore, setUsersHasMore] = useState(true);
  const [usersLoadingMore, setUsersLoadingMore] = useState(false);
  const usersLoadingMoreRef = useRef(false);
  const [userActioningId, setUserActioningId] = useState(() => new Set());
  const userActioningRef = useRef(new Set());
  const usersRequestRef = useRef(0);

  // ----- Designs -----
  const [designSearch, setDesignSearch] = useState("");
  const [designs, setDesigns] = useState([]);
  const [designsLoaded, setDesignsLoaded] = useState(false);
  // "all" = the searchable list of every design; "featured" = only the ones marked Featured.
  const [designsFilter, setDesignsFilter] = useState("all");
  const [designsLoading, setDesignsLoading] = useState(false);
  const [designsOffset, setDesignsOffset] = useState(0);
  const [designsHasMore, setDesignsHasMore] = useState(true);
  const [designsLoadingMore, setDesignsLoadingMore] = useState(false);
  const designsLoadingMoreRef = useRef(false);
  const [designActioningId, setDesignActioningId] = useState(() => new Set());
  const designActioningRef = useRef(new Set());
  const designsRequestRef = useRef(0);

  // ----- Comments -----
  const [commentSearch, setCommentSearch] = useState("");
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsOffset, setCommentsOffset] = useState(0);
  const [commentsHasMore, setCommentsHasMore] = useState(true);
  const [commentsLoadingMore, setCommentsLoadingMore] = useState(false);
  const commentsLoadingMoreRef = useRef(false);
  const [commentActioningId, setCommentActioningId] = useState(() => new Set());
  const commentActioningRef = useRef(new Set());
  const commentsRequestRef = useRef(0);

  // ----- Feedback -----
  const [feedback, setFeedback] = useState([]);
  const [feedbackLoaded, setFeedbackLoaded] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackOffset, setFeedbackOffset] = useState(0);
  const [feedbackHasMore, setFeedbackHasMore] = useState(true);
  const [feedbackLoadingMore, setFeedbackLoadingMore] = useState(false);
  const feedbackLoadingMoreRef = useRef(false);
  const feedbackRequestRef = useRef(0);

  // ----- Verification requests -----
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [verificationStatusFilter, setVerificationStatusFilter] = useState("pending");
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [verificationOffset, setVerificationOffset] = useState(0);
  const [verificationHasMore, setVerificationHasMore] = useState(true);
  const [verificationLoadingMore, setVerificationLoadingMore] = useState(false);
  const verificationLoadingMoreRef = useRef(false);
  const [verificationActioningId, setVerificationActioningId] = useState(() => new Set());
  const verificationActioningRef = useRef(new Set());
  const verificationRequestRef = useRef(0);
  const [verificationPendingCount, setVerificationPendingCount] = useState(0);

  // ----- Action log -----
  const [actionLog, setActionLog] = useState([]);
  const [logLoaded, setLogLoaded] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logOffset, setLogOffset] = useState(0);
  const [logHasMore, setLogHasMore] = useState(true);
  const [logLoadingMore, setLogLoadingMore] = useState(false);
  const logLoadingMoreRef = useRef(false);
  const logRequestRef = useRef(0);

  // ----- Stats -----
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // ----- Analytics -----
  const [growthStats, setGrowthStats] = useState(null);
  const [growthLoaded, setGrowthLoaded] = useState(false);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);
  const chartModalRef = useRef(null);
  useFocusTrap(chartModalRef, !!expandedChart);
  const [funnelStats, setFunnelStats] = useState(null);
  const [funnelLoaded, setFunnelLoaded] = useState(false);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [growthRangeDays, setGrowthRangeDays] = useState(7);

  // ----- Storage -----
  const [storageStats, setStorageStats] = useState(null);
  const [storageLoaded, setStorageLoaded] = useState(false);
  const [storageLoading, setStorageLoading] = useState(false);

  // ----- Settings (maintenance mode + broadcast) -----
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceLoaded, setMaintenanceLoaded] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  useEffect(() => {
    async function init() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      const u = session?.user ?? null;
      setUser(u);

      if (!u) {
        setIsAdmin(false);
        return;
      }

      // Via RPC, not a direct .select("is_admin") — that column is
      // deliberately not grantable to `authenticated` directly (so a
      // regular client can't query who else is an admin), which meant this
      // very check failed with "permission denied for table users" for
      // every admin, every time. am_i_admin() takes no parameters and only
      // ever answers for auth.uid() server-side — unlike is_admin_user(uid),
      // which takes a caller-supplied id and was never safe to expose
      // directly to the client (anyone could've probed any other user's
      // admin status with it).
      const { data, error } = await supabase.rpc("am_i_admin");

      if (error) {
        console.log("ADMIN CHECK ERROR:", error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
    }

    init();
  }, []);

  useEffect(() => {
    if (isAdmin !== true) return;
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, statusFilter]);

  useEffect(() => {
    if (isAdmin !== true) return;
    refreshPendingCount();
    refreshVerificationPendingCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function refreshPendingCount() {
    const { count, error } = await supabase
      .from("reports")
      .select("*", { count: "exact", head: true })
      .eq("status", "pending");

    if (error) {
      console.log("PENDING COUNT ERROR:", error);
      return;
    }

    setPendingCount(count || 0);
  }

  useEffect(() => {
    if (isAdmin !== true) return;
    if (view !== "verification") return;
    loadVerificationRequests(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, view, verificationStatusFilter]);

  async function refreshVerificationPendingCount() {
    const { count, error } = await supabase
      .from("verification_requests")
      .select("*", { count: "exact", head: true })
      .eq("status", "pending");

    if (error) {
      console.log("VERIFICATION PENDING COUNT ERROR:", error);
      return;
    }

    setVerificationPendingCount(count || 0);
  }

  useEffect(() => {
    if (isAdmin !== true) return;
    if (view === "users" && !usersLoaded) searchUsers(true);
    if (view === "designs" && !designsLoaded) searchDesigns(true);
    if (view === "comments" && !commentsLoaded) searchComments(true);
    if (view === "feedback" && !feedbackLoaded) loadFeedback(true);
    if (view === "log" && !logLoaded) loadActionLog(true);
    if (view === "stats" && !stats && !statsLoading) loadStats();
    if (view === "analytics" && !growthLoaded) loadGrowthStats();
    if (view === "analytics" && !funnelLoaded) loadFunnelStats();
    if (view === "storage" && !storageLoaded) loadStorageStats();
    if (view === "settings" && !maintenanceLoaded) loadMaintenanceMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, view]);

  // Only refetches on a toggle AFTER the tab has already loaded once — the
  // effect above already covers the very first load, and without the
  // usersLoaded guard this would fire an extra fetch on mount too.
  useEffect(() => {
    if (isAdmin !== true || !usersLoaded) return;
    searchUsers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersBannedOnly]);

  // Same lock-scroll + Escape-to-close convention as every other overlay in
  // the app (ShareButton, FollowListModal, ReportModal, ...).
  useEffect(() => {
    if (!expandedChart) return;

    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") setExpandedChart(null);
    }
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [expandedChart]);

  // ===================== Reports =====================

  // Fetches and enriches one page of reports — shared by the initial load
  // and "Load more", so a moderation backlog past the first page doesn't
  // just sit permanently invisible past a hard limit.
  async function fetchReportsPage(start, end) {
    // created_at alone isn't a stable sort for range()-based paging — two
    // reports landing in the same instant (a burst of reports on one design,
    // say) have no guaranteed relative order across separate page fetches,
    // which can skip or repeat rows on "Load more" (same class of bug fixed
    // in FollowListModal.jsx). id is unique, so adding it as a tiebreaker
    // makes every page a stable, non-overlapping slice.
    let query = supabase
      .from("reports")
      .select("*")
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .range(start, end);

    if (statusFilter !== "all") {
      query = query.eq("status", statusFilter);
    }

    const { data, error } = await query;

    if (error) {
      console.log("LOAD REPORTS ERROR:", error);
      return { rows: [], error };
    }

    const rows = data || [];

    const commentTargetIds = [...new Set(rows.filter(r => r.target_type === "comment").map(r => r.target_id))];
    // "design" reports used to be fetched nowhere at all — the card showed
    // no title, no owner, and Warn/Ban silently did nothing for them, since
    // targetUserIdFor had no case for this type despite it being the most
    // heavily used report button in the app (ReportDesignButton).
    const designTargetIds = [...new Set(rows.filter(r => r.target_type === "design").map(r => r.target_id))];

    const [{ data: targetComments }, { data: targetDesigns }] = await Promise.all([
      commentTargetIds.length > 0
        ? supabase.from("comments").select("id, comment, user_id").in("id", commentTargetIds)
        : Promise.resolve({ data: [] }),
      designTargetIds.length > 0
        ? supabase.from("designs").select("id, title, user_id").in("id", designTargetIds)
        : Promise.resolve({ data: [] })
    ]);

    const commentsById = {};
    (targetComments || []).forEach(c => (commentsById[c.id] = c));
    const designsById = {};
    (targetDesigns || []).forEach(d => (designsById[d.id] = d));

    // One shared username lookup — reporters, direct "user" report targets,
    // and the owners of reported comments/designs (needed to know who
    // Warn/Ban actually acts on and to show the admin who that is).
    const userTargetIds = rows.filter(r => r.target_type === "user").map(r => r.target_id);
    const commentOwnerIds = (targetComments || []).map(c => c.user_id);
    const designOwnerIds = (targetDesigns || []).map(d => d.user_id);
    const allUserIds = [...new Set([
      ...rows.map(r => r.reporter_id),
      ...userTargetIds,
      ...commentOwnerIds,
      ...designOwnerIds
    ].filter(Boolean))];

    // Via RPC — banned, like is_admin, isn't grantable to `authenticated`
    // directly (a regular client can't check who else is banned), and a
    // .select() that names even one restricted column fails entirely, not
    // just that column — which meant username came back empty too, and
    // every report showed "@unknown" instead of who it was actually about.
    // get_users_moderation_info() is admin-gated internally (via
    // am_i_admin()), so this only ever returns data for a real admin.
    const { data: users } = allUserIds.length > 0
      ? await supabase.rpc("get_users_moderation_info", { user_ids: allUserIds })
      : { data: [] };

    const usersById = {};
    (users || []).forEach(u => (usersById[u.id] = u));

    const merged = rows.map(r => {
      const targetComment = r.target_type === "comment" ? commentsById[r.target_id] : null;
      const targetDesign = r.target_type === "design" ? designsById[r.target_id] : null;
      return {
        ...r,
        reporter: usersById[r.reporter_id],
        targetUser: r.target_type === "user" ? usersById[r.target_id] : null,
        targetComment,
        targetCommentOwner: targetComment ? usersById[targetComment.user_id] : null,
        targetDesign,
        targetDesignOwner: targetDesign ? usersById[targetDesign.user_id] : null
      };
    });
    return { rows: merged, error: null };
  }

  async function loadReports() {
    const requestId = ++reportsRequestRef.current;
    setLoading(true);

    const { rows, error } = await fetchReportsPage(0, PAGE_SIZE - 1);

    // statusFilter (or a tab revisit) could have kicked off a newer load
    // while this one was still fetching — fetchReportsPage does several
    // awaited sub-queries, so the window for two of these to overlap and
    // resolve out of order is real. Discard this one if it's no longer the
    // latest rather than clobbering the newer, correct list.
    if (requestId !== reportsRequestRef.current) {
      setLoading(false);
      return;
    }

    // Matches Users/Designs' own tabs: a fetch failure bails out here
    // (fetchReportsPage already logged it) instead of treating it the same
    // as "zero reports" and marking the list falsely fully-loaded.
    if (error) {
      setLoading(false);
      return;
    }

    setReports(rows);
    setOffset(PAGE_SIZE);
    setHasMore(rows.length === PAGE_SIZE);
    setLoading(false);
  }

  async function loadMoreReports() {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    const requestId = reportsRequestRef.current;
    setLoadingMore(true);

    const { rows, error } = await fetchReportsPage(offset, offset + PAGE_SIZE - 1);

    // The filter changed (and reset the list) while this page was still in
    // flight — appending it now would tack stale-filter rows onto the new
    // list. Clearing loadingMore here too matters: left stuck true, every
    // future "Load more" click would return immediately (its own guard
    // checks loadingMore) and pagination on the new filter would be dead
    // until the page reloads.
    if (requestId !== reportsRequestRef.current) {
      setLoadingMore(false);
      loadingMoreRef.current = false;
      return;
    }

    // Same reasoning as loadReports above — bail on a real fetch failure
    // instead of silently treating it as "no more reports".
    if (error) {
      setLoadingMore(false);
      loadingMoreRef.current = false;
      return;
    }

    setReports(prev => [...prev, ...rows]);
    setOffset(prev => prev + PAGE_SIZE);
    setHasMore(rows.length === PAGE_SIZE);
    setLoadingMore(false);
    loadingMoreRef.current = false;
  }

  // Shared by every per-row "busy" ref/state pair below (actioningRef,
  // userActioningRef, etc.) — keeps the ref (checked synchronously, so a
  // fast double-click can't slip through before the next render) and the
  // state (drives disabled={xId.has(id)} in the JSX) in lockstep.
  function beginAction(ref, setId, id) {
    ref.current.add(id);
    setId(prev => new Set(prev).add(id));
  }
  function endAction(ref, setId, id) {
    ref.current.delete(id);
    setId(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function targetUserIdFor(report) {
    if (report.target_type === "user") return report.target_id;
    if (report.target_type === "comment") return report.targetComment?.user_id || null;
    if (report.target_type === "design") return report.targetDesign?.user_id || null;
    return null;
  }

  async function dismissReport(id) {
    if (actioningRef.current.has(id)) return;
    beginAction(actioningRef, setActioningId, id);

    const { error } = await supabase.rpc("admin_update_report_status", {
      target_report_id: id,
      new_status: "dismissed"
    });

    endAction(actioningRef, setActioningId, id);

    if (error) {
      console.log("DISMISS REPORT ERROR:", error);
      return;
    }

    setReports(prev => prev.filter(r => r.id !== id));
    refreshPendingCount();
  }

  async function warnUser(report) {
    if (actioningRef.current.has(report.id)) return;

    const targetUserId = targetUserIdFor(report);
    if (!targetUserId) return;

    beginAction(actioningRef, setActioningId, report.id);

    // createNotification is normally best-effort (see utils/notifications.js)
    // since it's a side effect of an action that already succeeded on its
    // own — here it IS the action, so a silent failure (e.g. rejected by
    // RLS) has to stop this from marking the report handled when no warning
    // actually went out.
    const { error: notifyError } = await createNotification({
      userId: targetUserId,
      actorId: user.id,
      type: "warning",
      commentText: "You've received a warning from moderators for violating our community guidelines."
    });

    if (notifyError) {
      console.log("WARN USER ERROR:", notifyError);
      endAction(actioningRef, setActioningId, report.id);
      return;
    }

    const { error } = await supabase.rpc("admin_update_report_status", {
      target_report_id: report.id,
      new_status: "actioned"
    });

    endAction(actioningRef, setActioningId, report.id);

    if (error) {
      console.log("WARN USER ERROR:", error);
      return;
    }

    setReports(prev => prev.filter(r => r.id !== report.id));
    refreshPendingCount();
  }

  async function banUser(report) {
    if (actioningRef.current.has(report.id)) return;

    const targetUserId = targetUserIdFor(report);
    if (!targetUserId) return;

    // admin_ban_user only flips the banned flag in the DB — it can't revoke
    // an already-issued JWT, so this wasn't accurate: the account keeps
    // working under its current session for up to an hour (see Navbar.jsx's
    // banned check, which only runs on the next auth event) before they're
    // actually signed out. This still isn't fixed, just described honestly.
    const ok = window.confirm(
      "Ban this user? They won't be able to log back in, but if they're already using the app, it can take up to an hour before they're signed out of their current session."
    );
    if (!ok) return;

    beginAction(actioningRef, setActioningId, report.id);

    // Via RPC — the users table has a Supabase-managed Data API restriction
    // that blocks a direct table UPDATE even for RLS-permitted admin
    // actions, which silently broke this button entirely.
    const { error: banError } = await supabase.rpc("admin_ban_user", { target_user_id: targetUserId });

    if (banError) {
      console.log("BAN USER ERROR:", banError);
      endAction(actioningRef, setActioningId, report.id);
      return;
    }

    const { error } = await supabase.rpc("admin_update_report_status", {
      target_report_id: report.id,
      new_status: "actioned"
    });

    endAction(actioningRef, setActioningId, report.id);

    if (error) {
      console.log("MARK REPORT ACTIONED ERROR:", error);
      return;
    }

    setReports(prev => prev.filter(r => r.id !== report.id));
    refreshPendingCount();
  }

  // ===================== Users =====================

  async function searchUsers(reset) {
    let requestId;
    if (reset) {
      requestId = ++usersRequestRef.current;
      setUsersLoading(true);
    } else {
      if (usersLoadingMoreRef.current || !usersHasMore) return;
      usersLoadingMoreRef.current = true;
      requestId = usersRequestRef.current;
      setUsersLoadingMore(true);
    }

    const start = reset ? 0 : usersOffset;

    const { data, error } = await supabase.rpc("admin_search_users", {
      search_term: userSearch.trim(),
      limit_count: USER_PAGE_SIZE,
      offset_count: start,
      banned_only: usersBannedOnly
    });

    // A newer search/toggle/reset fired while this one was in flight —
    // let that one own the result instead of this stale one overwriting it.
    // Still have to clear our own loading flags here though: leaving
    // usersLoadingMore stuck true would make every future "Load more"
    // click return immediately (its own guard checks usersLoadingMore),
    // permanently breaking pagination until the page is reloaded.
    if (requestId !== usersRequestRef.current) {
      setUsersLoading(false);
      setUsersLoadingMore(false);
      usersLoadingMoreRef.current = false;
      return;
    }

    if (error) {
      console.log("SEARCH USERS ERROR:", error);
      setUsersLoading(false);
      setUsersLoadingMore(false);
      usersLoadingMoreRef.current = false;
      return;
    }

    const rows = data || [];
    setUsers(prev => (reset ? rows : [...prev, ...rows]));
    setUsersOffset(start + USER_PAGE_SIZE);
    setUsersHasMore(rows.length === USER_PAGE_SIZE);
    setUsersLoaded(true);
    setUsersLoading(false);
    setUsersLoadingMore(false);
    usersLoadingMoreRef.current = false;
  }

  function handleUserSearchSubmit(e) {
    e.preventDefault();
    searchUsers(true);
  }

  async function toggleBan(target) {
    if (userActioningRef.current.has(target.id)) return;
    if (target.id === user.id) return; // can't ban/unban yourself here

    const willBan = !target.banned;

    if (willBan) {
      const ok = window.confirm(
        `Ban @${target.username}? They won't be able to log back in, but if they're already using the app, it can take up to an hour before they're signed out of their current session.`
      );
      if (!ok) return;
    }

    beginAction(userActioningRef, setUserActioningId, target.id);

    const { error } = await supabase.rpc("admin_ban_user", { target_user_id: target.id, ban_status: willBan });

    endAction(userActioningRef, setUserActioningId, target.id);

    if (error) {
      console.log("TOGGLE BAN ERROR:", error);
      return;
    }

    setUsers(prev => prev.map(u => (u.id === target.id ? { ...u, banned: willBan } : u)));
  }

  async function toggleAdmin(target) {
    if (userActioningRef.current.has(target.id)) return;
    if (target.id === user.id) return; // can't remove your own admin access here

    const willBeAdmin = !target.is_admin;
    const ok = window.confirm(
      willBeAdmin
        ? `Make @${target.username} an admin? They'll be able to see and act on all reports, and ban, unban, or promote other users.`
        : `Remove @${target.username} as admin?`
    );
    if (!ok) return;

    beginAction(userActioningRef, setUserActioningId, target.id);

    const { error } = await supabase.rpc("admin_set_is_admin", {
      target_user_id: target.id,
      new_value: willBeAdmin
    });

    endAction(userActioningRef, setUserActioningId, target.id);

    if (error) {
      console.log("TOGGLE ADMIN ERROR:", error);
      window.alert(error.message || "Couldn't update admin status.");
      return;
    }

    setUsers(prev => prev.map(u => (u.id === target.id ? { ...u, is_admin: willBeAdmin } : u)));
  }

  async function toggleVerified(target) {
    if (userActioningRef.current.has(target.id)) return;

    const willBeVerified = !target.verified;

    beginAction(userActioningRef, setUserActioningId, target.id);

    const { error } = await supabase.rpc("admin_set_verified", {
      target_user_id: target.id,
      new_value: willBeVerified
    });

    endAction(userActioningRef, setUserActioningId, target.id);

    if (error) {
      console.log("TOGGLE VERIFIED ERROR:", error);
      window.alert(error.message || "Couldn't update verified status.");
      return;
    }

    setUsers(prev => prev.map(u => (u.id === target.id ? { ...u, verified: willBeVerified } : u)));
  }

  // ===================== Designs =====================

  async function searchDesigns(reset) {
    let requestId;
    if (reset) {
      requestId = ++designsRequestRef.current;
      setDesignsLoading(true);
    } else {
      if (designsLoadingMoreRef.current || !designsHasMore) return;
      designsLoadingMoreRef.current = true;
      requestId = designsRequestRef.current;
      setDesignsLoadingMore(true);
    }

    const start = reset ? 0 : designsOffset;

    const { data, error } = await supabase.rpc("admin_search_designs", {
      search_term: designSearch.trim(),
      limit_count: DESIGN_PAGE_SIZE,
      offset_count: start
    });

    if (requestId !== designsRequestRef.current) {
      setDesignsLoading(false);
      setDesignsLoadingMore(false);
      designsLoadingMoreRef.current = false;
      return;
    }

    if (error) {
      console.log("SEARCH DESIGNS ERROR:", error);
      setDesignsLoading(false);
      setDesignsLoadingMore(false);
      designsLoadingMoreRef.current = false;
      return;
    }

    const rows = data || [];
    setDesigns(prev => (reset ? rows : [...prev, ...rows]));
    setDesignsOffset(start + DESIGN_PAGE_SIZE);
    setDesignsHasMore(rows.length === DESIGN_PAGE_SIZE);
    setDesignsLoaded(true);
    setDesignsLoading(false);
    setDesignsLoadingMore(false);
    designsLoadingMoreRef.current = false;
  }

  function handleDesignSearchSubmit(e) {
    e.preventDefault();
    searchDesigns(true);
  }

  // Every design marked Featured, newest first, in the same row shape the RPC returns. There
  // are few of them, so one request with no "load more" (the 500 is only a safety cap).
  async function loadFeaturedDesigns() {
    const requestId = ++designsRequestRef.current;
    setDesignsLoading(true);

    const { data, error } = await supabase
      .from("designs")
      .select("id, title, image_url, created_at, featured, users(username)")
      .eq("featured", true)
      .order("created_at", { ascending: false })
      .limit(500);

    if (requestId !== designsRequestRef.current) return;

    if (error) {
      console.log("LOAD FEATURED DESIGNS ERROR:", error);
      // Don't leave the full list sitting under the "Featured only" button.
      setDesigns([]);
      setDesignsHasMore(false);
      setDesignsLoading(false);
      return;
    }

    setDesigns((data || []).map(d => ({ ...d, username: d.users?.username })));
    setDesignsHasMore(false);
    setDesignsLoaded(true);
    setDesignsLoading(false);
  }

  function chooseDesignsFilter(next) {
    if (next === designsFilter) return;
    setDesignsFilter(next);
    if (next === "featured") loadFeaturedDesigns();
    else searchDesigns(true);
  }

  async function deleteDesign(design) {
    if (designActioningRef.current.has(design.id)) return;

    const ok = window.confirm(`Delete "${design.title || "this design"}" by @${design.username}? This can't be undone.`);
    if (!ok) return;

    beginAction(designActioningRef, setDesignActioningId, design.id);

    const { error } = await supabase.rpc("admin_delete_design", { target_design_id: design.id });

    endAction(designActioningRef, setDesignActioningId, design.id);

    if (error) {
      console.log("DELETE DESIGN ERROR:", error);
      window.alert(error.message || "Couldn't delete that design.");
      return;
    }

    setDesigns(prev => prev.filter(d => d.id !== design.id));
  }

  async function toggleFeatured(design) {
    if (designActioningRef.current.has(design.id)) return;

    const willFeature = !design.featured;

    beginAction(designActioningRef, setDesignActioningId, design.id);

    const { error } = await supabase.rpc("admin_set_featured", {
      target_design_id: design.id,
      new_value: willFeature
    });

    endAction(designActioningRef, setDesignActioningId, design.id);

    if (error) {
      console.log("TOGGLE FEATURED ERROR:", error);
      window.alert(error.message || "Couldn't update featured status.");
      return;
    }

    setDesigns(prev => prev.map(d => (d.id === design.id ? { ...d, featured: willFeature } : d)));
  }

  // ===================== Comments =====================

  async function searchComments(reset) {
    let requestId;
    if (reset) {
      requestId = ++commentsRequestRef.current;
      setCommentsLoading(true);
    } else {
      if (commentsLoadingMoreRef.current || !commentsHasMore) return;
      commentsLoadingMoreRef.current = true;
      requestId = commentsRequestRef.current;
      setCommentsLoadingMore(true);
    }

    const start = reset ? 0 : commentsOffset;

    const { data, error } = await supabase.rpc("admin_search_comments", {
      search_term: commentSearch.trim(),
      limit_count: COMMENT_PAGE_SIZE,
      offset_count: start
    });

    if (requestId !== commentsRequestRef.current) {
      setCommentsLoading(false);
      setCommentsLoadingMore(false);
      commentsLoadingMoreRef.current = false;
      return;
    }

    if (error) {
      console.log("SEARCH COMMENTS ERROR:", error);
      setCommentsLoading(false);
      setCommentsLoadingMore(false);
      commentsLoadingMoreRef.current = false;
      return;
    }

    const rows = data || [];
    setComments(prev => (reset ? rows : [...prev, ...rows]));
    setCommentsOffset(start + COMMENT_PAGE_SIZE);
    setCommentsHasMore(rows.length === COMMENT_PAGE_SIZE);
    setCommentsLoaded(true);
    setCommentsLoading(false);
    setCommentsLoadingMore(false);
    commentsLoadingMoreRef.current = false;
  }

  function handleCommentSearchSubmit(e) {
    e.preventDefault();
    searchComments(true);
  }

  async function deleteComment(c) {
    if (commentActioningRef.current.has(c.id)) return;

    const ok = window.confirm(`Delete this comment by @${c.username}? This can't be undone.`);
    if (!ok) return;

    beginAction(commentActioningRef, setCommentActioningId, c.id);

    const { error } = await supabase.rpc("admin_delete_comment", { target_comment_id: c.id });

    endAction(commentActioningRef, setCommentActioningId, c.id);

    if (error) {
      console.log("DELETE COMMENT ERROR:", error);
      window.alert(error.message || "Couldn't delete that comment.");
      return;
    }

    setComments(prev => prev.filter(x => x.id !== c.id));
  }

  // ===================== Feedback =====================

  async function loadFeedback(reset) {
    let requestId;
    if (reset) {
      requestId = ++feedbackRequestRef.current;
      setFeedbackLoading(true);
    } else {
      if (feedbackLoadingMoreRef.current || !feedbackHasMore) return;
      feedbackLoadingMoreRef.current = true;
      requestId = feedbackRequestRef.current;
      setFeedbackLoadingMore(true);
    }

    const start = reset ? 0 : feedbackOffset;

    const { data, error } = await supabase.rpc("admin_get_feedback", {
      limit_count: FEEDBACK_PAGE_SIZE,
      offset_count: start
    });

    if (requestId !== feedbackRequestRef.current) {
      setFeedbackLoading(false);
      setFeedbackLoadingMore(false);
      feedbackLoadingMoreRef.current = false;
      return;
    }

    if (error) {
      console.log("LOAD FEEDBACK ERROR:", error);
      setFeedbackLoading(false);
      setFeedbackLoadingMore(false);
      feedbackLoadingMoreRef.current = false;
      return;
    }

    const rows = data || [];
    setFeedback(prev => (reset ? rows : [...prev, ...rows]));
    setFeedbackOffset(start + FEEDBACK_PAGE_SIZE);
    setFeedbackHasMore(rows.length === FEEDBACK_PAGE_SIZE);
    setFeedbackLoaded(true);
    setFeedbackLoading(false);
    setFeedbackLoadingMore(false);
    feedbackLoadingMoreRef.current = false;
  }

  // ===================== Verification requests =====================

  async function loadVerificationRequests(reset) {
    let requestId;
    if (reset) {
      requestId = ++verificationRequestRef.current;
      setVerificationLoading(true);
    } else {
      if (verificationLoadingMoreRef.current || !verificationHasMore) return;
      verificationLoadingMoreRef.current = true;
      requestId = verificationRequestRef.current;
      setVerificationLoadingMore(true);
    }

    const start = reset ? 0 : verificationOffset;
    const end = start + VERIFICATION_PAGE_SIZE - 1;

    let query = supabase
      .from("verification_requests")
      .select("*")
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .range(start, end);

    if (verificationStatusFilter !== "all") {
      query = query.eq("status", verificationStatusFilter);
    }

    const { data, error } = await query;

    if (requestId !== verificationRequestRef.current) {
      setVerificationLoading(false);
      setVerificationLoadingMore(false);
      verificationLoadingMoreRef.current = false;
      return;
    }

    if (error) {
      console.log("LOAD VERIFICATION REQUESTS ERROR:", error);
      setVerificationLoading(false);
      setVerificationLoadingMore(false);
      verificationLoadingMoreRef.current = false;
      return;
    }

    const rows = data || [];
    const userIds = [...new Set(rows.map(r => r.user_id).filter(Boolean))];

    const { data: requesters } = userIds.length > 0
      ? await supabase.rpc("get_verification_requesters_info", { user_ids: userIds })
      : { data: [] };

    const requestersById = {};
    (requesters || []).forEach(u => (requestersById[u.id] = u));

    const merged = rows.map(r => ({ ...r, requester: requestersById[r.user_id] }));

    setVerificationRequests(prev => (reset ? merged : [...prev, ...merged]));
    setVerificationOffset(start + VERIFICATION_PAGE_SIZE);
    setVerificationHasMore(rows.length === VERIFICATION_PAGE_SIZE);
    setVerificationLoading(false);
    setVerificationLoadingMore(false);
    verificationLoadingMoreRef.current = false;
  }

  async function reviewVerificationRequest(id, approve) {
    if (verificationActioningRef.current.has(id)) return;
    beginAction(verificationActioningRef, setVerificationActioningId, id);

    const { error } = await supabase.rpc("admin_review_verification_request", {
      request_id: id,
      approve
    });

    endAction(verificationActioningRef, setVerificationActioningId, id);

    if (error) {
      console.log("REVIEW VERIFICATION REQUEST ERROR:", error);
      window.alert(error.message || "Couldn't review this request.");
      return;
    }

    setVerificationRequests(prev => prev.filter(r => r.id !== id));
    refreshVerificationPendingCount();
  }

  // ===================== Action log =====================

  async function loadActionLog(reset) {
    let requestId;
    if (reset) {
      requestId = ++logRequestRef.current;
      setLogLoading(true);
    } else {
      if (logLoadingMoreRef.current || !logHasMore) return;
      logLoadingMoreRef.current = true;
      requestId = logRequestRef.current;
      setLogLoadingMore(true);
    }

    const start = reset ? 0 : logOffset;

    const { data, error } = await supabase.rpc("admin_get_action_log", {
      limit_count: LOG_PAGE_SIZE,
      offset_count: start
    });

    if (requestId !== logRequestRef.current) {
      setLogLoading(false);
      setLogLoadingMore(false);
      logLoadingMoreRef.current = false;
      return;
    }

    if (error) {
      console.log("LOAD ACTION LOG ERROR:", error);
      setLogLoading(false);
      setLogLoadingMore(false);
      logLoadingMoreRef.current = false;
      return;
    }

    const rows = data || [];
    setActionLog(prev => (reset ? rows : [...prev, ...rows]));
    setLogOffset(start + LOG_PAGE_SIZE);
    setLogHasMore(rows.length === LOG_PAGE_SIZE);
    setLogLoaded(true);
    setLogLoading(false);
    setLogLoadingMore(false);
    logLoadingMoreRef.current = false;
  }

  function actionLabel(action) {
    switch (action) {
      case "ban_user":
        return "banned";
      case "unban_user":
        return "unbanned";
      case "promote_admin":
        return "made admin";
      case "demote_admin":
        return "removed admin from";
      case "warn_user":
        return "warned";
      case "delete_design":
        return "deleted a design by";
      case "delete_comment":
        return "deleted a comment by";
      case "verify_user":
        return "verified";
      case "unverify_user":
        return "removed the verified badge from";
      case "feature_design":
        return "featured a design by";
      case "unfeature_design":
        return "unfeatured a design by";
      case "enable_maintenance":
        return "turned ON maintenance mode";
      case "disable_maintenance":
        return "turned OFF maintenance mode";
      case "broadcast_announcement":
        return "sent an announcement to everyone:";
      default:
        return action;
    }
  }

  // ===================== Stats =====================

  async function loadStats() {
    setStatsLoading(true);

    const { data, error } = await supabase.rpc("admin_get_stats");

    if (error) {
      console.log("LOAD STATS ERROR:", error);
      setStatsLoading(false);
      return;
    }

    setStats(Array.isArray(data) ? data[0] : data);
    setStatsLoading(false);
  }

  // Jumping here from a Stats card should land on the report list already
  // filtered to match what was clicked, not just on the Reports tab in
  // whatever filter state it was last left in.
  function goToReportsFiltered(status) {
    setStatusFilter(status);
    setView("reports");
  }

  // ===================== Analytics =====================

  const GROWTH_RANGES = [
    { label: "Day", days: 1 },
    { label: "Week", days: 7 },
    { label: "Month", days: 30 },
    { label: "Year", days: 365 }
  ];

  async function loadGrowthStats(days = growthRangeDays) {
    setGrowthLoading(true);

    const { data, error } = await supabase.rpc("admin_get_growth_stats", { days });

    if (error) {
      console.log("LOAD GROWTH STATS ERROR:", error);
      setGrowthLoading(false);
      return;
    }

    setGrowthStats(data || []);
    setGrowthLoaded(true);
    setGrowthLoading(false);
  }

  function changeGrowthRange(days) {
    setGrowthRangeDays(days);
    loadGrowthStats(days);
    loadFunnelStats(days);
  }

  function funnelRangeLabel(days) {
    if (days === 1) return "today";
    if (days === 7) return "last 7 days";
    if (days === 30) return "last 30 days";
    return "last 12 months";
  }

  // Ordered stage-by-stage so the funnel panel below always reads top-to-
  // bottom in the order someone actually moves through the wizard, not
  // whatever order the RPC's GROUP BY happens to return.
  const FUNNEL_STAGE_ORDER = [
    { event: "signup_started", label: "Opened signup" },
    { event: "signup_otp_sent", label: "Email code sent" },
    { event: "signup_google_clicked", label: "Clicked “Continue with Google”" },
    { event: "signup_birthdate_reached", label: "Verified (email code or Google)" },
    { event: "signup_username_reached", label: "Birthdate entered" },
    { event: "signup_gender_reached", label: "Username chosen" },
    { event: "signup_password_reached", label: "Gender step (email accounts only)" },
    { event: "signup_completed", label: "Account created" }
  ];

  async function loadFunnelStats(days = growthRangeDays) {
    setFunnelLoading(true);

    const { data, error } = await supabase.rpc("admin_get_signup_funnel", { days });

    if (error) {
      console.log("LOAD FUNNEL STATS ERROR:", error);
      setFunnelLoading(false);
      return;
    }

    const byEvent = Object.fromEntries((data || []).map(row => [row.event, row.sessions]));
    setFunnelStats(FUNNEL_STAGE_ORDER.map(stage => ({ ...stage, sessions: byEvent[stage.event] || 0 })));
    setFunnelLoaded(true);
    setFunnelLoading(false);
  }

  // ===================== Storage =====================

  async function loadStorageStats() {
    setStorageLoading(true);

    const { data, error } = await supabase.rpc("admin_get_storage_stats");

    if (error) {
      console.log("LOAD STORAGE STATS ERROR:", error);
      setStorageLoading(false);
      return;
    }

    setStorageStats(data || []);
    setStorageLoaded(true);
    setStorageLoading(false);
  }

  function formatBytes(bytes) {
    if (!bytes) return "0 MB";
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  }

  // ===================== Settings =====================

  // site_settings is publicly readable (App.js needs to check it for every
  // visitor, logged in or not) — only the write side goes through the
  // admin-gated RPC below.
  async function loadMaintenanceMode() {
    setMaintenanceLoading(true);

    const { data, error } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", "maintenance_mode")
      .maybeSingle();

    if (error) {
      console.log("LOAD MAINTENANCE MODE ERROR:", error);
      setMaintenanceLoading(false);
      return;
    }

    setMaintenanceEnabled(!!data?.value?.enabled);
    setMaintenanceMessage(data?.value?.message || "");
    setMaintenanceLoaded(true);
    setMaintenanceLoading(false);
  }

  async function saveMaintenanceMode(nextEnabled) {
    if (maintenanceSaving) return;
    setMaintenanceSaving(true);

    const { error } = await supabase.rpc("admin_set_maintenance_mode", {
      enabled: nextEnabled,
      message: maintenanceMessage.trim()
    });

    setMaintenanceSaving(false);

    if (error) {
      console.log("SAVE MAINTENANCE MODE ERROR:", error);
      window.alert(error.message || "Couldn't update maintenance mode.");
      return;
    }

    setMaintenanceEnabled(nextEnabled);
  }

  async function sendBroadcast(e) {
    e.preventDefault();
    if (broadcastSending) return;

    const clean = broadcastMessage.trim();
    if (!clean) return;

    const ok = window.confirm("Send this message to every user? This can't be undone or unsent.");
    if (!ok) return;

    setBroadcastSending(true);
    setBroadcastResult(null);

    const { data, error } = await supabase.rpc("admin_broadcast_announcement", { message: clean });

    setBroadcastSending(false);

    if (error) {
      console.log("BROADCAST ERROR:", error);
      window.alert(error.message || "Couldn't send that announcement.");
      return;
    }

    setBroadcastResult(`Sent to ${data} user${data === 1 ? "" : "s"}.`);
    setBroadcastMessage("");
  }

  if (isAdmin === null) {
    return <p style={mutedText}>Loading…</p>;
  }

  if (!isAdmin) {
    return <p style={mutedText}>You don't have access to this page.</p>;
  }

  return (
    <div style={page} className="admin-page">
      {/* Mobile: tabs as a wrapping pill row across the top, single content
          column below — matches the rest of the app's mobile-first cards.
          Desktop (900px+): a fixed left sidebar instead, since there's
          finally room for full labels in a vertical list without wrapping
          into an ever-taller pill grid, and a wider content column that
          actually uses the screen instead of a phone-width strip floating
          in the middle of a monitor. */}
      <style>{`
        .admin-page { max-width: 720px; margin: 0 auto; }
        .admin-layout { display: flex; flex-direction: column; }
        .admin-content { min-width: 0; }
        /* Was a plain inline style prop before (display/flexDirection/gap)
           on the same elements — an inline style always wins over a class
           rule for the same property, media query or not, so the desktop
           grid override below could never actually take effect while that
           inline style was still setting display:flex itself. Moving the
           base (mobile) rule here too, not just the override, is what
           actually lets the override work. */
        .admin-list { display: flex; flex-direction: column; gap: 14px; }
        @media (min-width: 900px) {
          .admin-page { max-width: none; width: 100%; padding-left: 16px; padding-right: 16px; box-sizing: border-box; }
          /* Full-width, like a native desktop app's own window (File
             Explorer's file list stretches the same way) — content that's
             actually a grid (Users/Designs/etc.) fills that width with more
             columns via .admin-list below; a genuinely short view (an empty
             report list) will just show blank space after it, same as any
             other app's empty state, rather than being artificially capped
             or centered to hide that. */
          .admin-layout { flex-direction: row; align-items: flex-start; gap: 32px; }
          .admin-nav {
            flex-direction: column;
            flex-wrap: nowrap;
            width: 210px;
            flex-shrink: 0;
            border-bottom: none;
            border-right: 1px solid var(--border-soft);
            padding-bottom: 0;
            padding-right: 20px;
            position: sticky;
            top: 20px;
          }
          .admin-nav button { width: 100%; justify-content: flex-start; }
          .admin-content { flex: 1; min-width: 0; }
          .admin-stats-grid { max-width: 1400px; }
          .admin-chart-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; max-width: 1400px; align-items: start; }
          .admin-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); align-items: start; }
          .admin-list > button { grid-column: 1 / -1; }
        }
      `}</style>

      <h1 style={heading}>
        <IoShieldOutline style={{ verticalAlign: -3, marginRight: 8 }} />
        Admin
      </h1>

      <div className="admin-layout">
        <div className="admin-nav" style={tabRow}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = view === t.key;
            return (
              <button key={t.key} style={active ? tabBtnActive : tabBtn} onClick={() => setView(t.key)}>
                <Icon style={{ verticalAlign: -2, marginRight: 6, fontSize: 15 }} />
                {t.label}
                {t.key === "reports" && pendingCount > 0 && (
                  <span style={active ? tabBadgeActive : tabBadge}>{pendingCount > 99 ? "99+" : pendingCount}</span>
                )}
                {t.key === "verification" && verificationPendingCount > 0 && (
                  <span style={active ? tabBadgeActive : tabBadge}>
                    {verificationPendingCount > 99 ? "99+" : verificationPendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="admin-content">
      {view === "reports" && (
        <>
          <div style={filterRow}>
            {["pending", "actioned", "dismissed", "all"].map(s => (
              <button
                key={s}
                style={statusFilter === s ? filterBtnActive : filterBtn}
                onClick={() => setStatusFilter(s)}
              >
                {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {loading ? (
            <p style={mutedText}>Loading reports…</p>
          ) : reports.length === 0 ? (
            <p style={mutedText}>No {statusFilter === "all" ? "" : statusFilter} reports.</p>
          ) : (
            <div className="admin-list">
              {reports.map(r => (
                <div key={r.id} style={card}>
                  <div style={cardHeader}>
                    <span style={typeBadge}>{r.target_type}</span>
                    <span style={metaText}>
                      {r.status !== "pending" && <span style={statusTag}>{r.status} · </span>}
                      {timeAgo(r.created_at)}
                    </span>
                  </div>

                  <p style={lineText}>Reported by @{r.reporter?.username || "unknown"}</p>

                  {r.target_type === "user" && (
                    <p style={lineText}>
                      Target: @{r.targetUser?.username || "unknown user"}
                      {r.targetUser?.banned && <span style={bannedTag}> (already banned)</span>}
                    </p>
                  )}

                  {r.target_type === "comment" && (
                    <>
                      <p style={lineText}>
                        By @{r.targetCommentOwner?.username || "unknown user"}
                        {r.targetCommentOwner?.banned && <span style={bannedTag}> (already banned)</span>}
                      </p>
                      <p style={lineText}>Comment: "{r.targetComment?.comment || "(deleted)"}"</p>
                    </>
                  )}

                  {r.target_type === "design" && (
                    <p style={lineText}>
                      Design: {r.targetDesign?.title ? `"${r.targetDesign.title}"` : "(deleted)"} by @
                      {r.targetDesignOwner?.username || "unknown user"}
                      {r.targetDesignOwner?.banned && <span style={bannedTag}> (already banned)</span>}
                    </p>
                  )}

                  {r.reason && <p style={reasonText}>Reason: {r.reason}</p>}

                  {r.status === "pending" && (
                    <div style={actionsRow}>
                      <button style={dismissBtn} disabled={actioningId.has(r.id)} onClick={() => dismissReport(r.id)}>
                        Dismiss
                      </button>
                      <button style={warnBtn} disabled={actioningId.has(r.id)} onClick={() => warnUser(r)}>
                        Warn user
                      </button>
                      <button style={banBtn} disabled={actioningId.has(r.id)} onClick={() => banUser(r)}>
                        Ban user
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {hasMore && (
                <button style={dismissBtn} disabled={loadingMore} onClick={loadMoreReports}>
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {view === "users" && (
        <>
          <form onSubmit={handleUserSearchSubmit} style={searchRow}>
            <input
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search by username…"
              style={searchInput}
            />
            <button type="submit" style={dismissBtn} disabled={usersLoading}>
              Search
            </button>
          </form>

          <div style={filterRow}>
            <button
              style={usersBannedOnly ? filterBtnActive : filterBtn}
              onClick={() => setUsersBannedOnly(v => !v)}
            >
              Banned only
            </button>
          </div>

          {usersLoading ? (
            <p style={mutedText}>Loading users…</p>
          ) : users.length === 0 ? (
            <p style={mutedText}>No users found.</p>
          ) : (
            <>
              <div className="admin-list">
                {users.map(u => {
                  const isSelf = u.id === user.id;
                  const busy = userActioningId.has(u.id);
                  return (
                    <div key={u.id} style={card}>
                      <div style={userRow}>
                        <img src={u.avatar_url || "/default-avatar.svg"} alt="" style={userAvatar} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={lineText}>
                            @{u.username}
                            {isSelf && <span style={metaText}> (you)</span>}
                          </p>
                          <span style={metaText}>
                            Joined {new Date(u.created_at).toLocaleDateString()}
                            {u.is_admin && <span style={adminTag}> · Admin</span>}
                            {u.verified && <span style={verifiedTag}> · Verified</span>}
                            {u.banned && <span style={bannedTag}> · Banned</span>}
                          </span>
                        </div>
                      </div>

                      <div style={actionsRow}>
                        <button
                          style={u.banned ? dismissBtn : banBtn}
                          disabled={busy || isSelf}
                          onClick={() => toggleBan(u)}
                        >
                          {u.banned ? "Unban" : "Ban"}
                        </button>
                        <button
                          style={u.is_admin ? dismissBtn : warnBtn}
                          disabled={busy || isSelf}
                          onClick={() => toggleAdmin(u)}
                        >
                          {u.is_admin ? "Remove admin" : "Make admin"}
                        </button>
                        <button
                          style={u.verified ? dismissBtn : verifyBtn}
                          disabled={busy}
                          onClick={() => toggleVerified(u)}
                        >
                          {u.verified ? "Unverify" : "Verify"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {usersHasMore && (
                <button style={dismissBtn} disabled={usersLoadingMore} onClick={() => searchUsers(false)}>
                  {usersLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </>
          )}
        </>
      )}

      {view === "verification" && (
        <>
          <div style={filterRow}>
            {["pending", "approved", "rejected", "all"].map(s => (
              <button
                key={s}
                style={verificationStatusFilter === s ? filterBtnActive : filterBtn}
                onClick={() => setVerificationStatusFilter(s)}
              >
                {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {verificationLoading ? (
            <p style={mutedText}>Loading verification requests…</p>
          ) : verificationRequests.length === 0 ? (
            <p style={mutedText}>No {verificationStatusFilter === "all" ? "" : verificationStatusFilter} requests.</p>
          ) : (
            <div className="admin-list">
              {verificationRequests.map(r => {
                const busy = verificationActioningId.has(r.id);
                return (
                  <div key={r.id} style={card}>
                    <div style={userRow}>
                      <img src={r.requester?.avatar_url || "/default-avatar.svg"} alt="" style={userAvatar} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={lineText}>
                          @{r.requester?.username || "unknown"}
                          {r.requester?.verified && <span style={verifiedTag}> · Already verified</span>}
                        </p>
                        <span style={metaText}>
                          {r.status !== "pending" && <span style={statusTag}>{r.status} · </span>}
                          {timeAgo(r.created_at)}
                        </span>
                      </div>
                    </div>

                    <p style={reasonText}>{r.reason}</p>

                    {r.status === "pending" && (
                      <div style={actionsRow}>
                        <button style={banBtn} disabled={busy} onClick={() => reviewVerificationRequest(r.id, false)}>
                          Reject
                        </button>
                        <button style={verifyBtn} disabled={busy} onClick={() => reviewVerificationRequest(r.id, true)}>
                          Approve
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {verificationHasMore && (
                <button
                  style={dismissBtn}
                  disabled={verificationLoadingMore}
                  onClick={() => loadVerificationRequests(false)}
                >
                  {verificationLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {view === "designs" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <button
              style={designsFilter === "all" ? tabBtnActive : tabBtn}
              onClick={() => chooseDesignsFilter("all")}
            >
              All designs
            </button>
            <button
              style={designsFilter === "featured" ? tabBtnActive : tabBtn}
              onClick={() => chooseDesignsFilter("featured")}
            >
              Featured only
            </button>
          </div>

          {designsFilter === "all" && (
            <form onSubmit={handleDesignSearchSubmit} style={searchRow}>
              <input
                value={designSearch}
                onChange={e => setDesignSearch(e.target.value)}
                placeholder="Search by title or username…"
                style={searchInput}
              />
              <button type="submit" style={dismissBtn} disabled={designsLoading}>
                Search
              </button>
            </form>
          )}

          {designsLoading ? (
            <p style={mutedText}>Loading designs…</p>
          ) : designs.length === 0 ? (
            <p style={mutedText}>{designsFilter === "featured" ? "No featured designs yet." : "No designs found."}</p>
          ) : (
            <>
              <div className="admin-list">
                {designs.map(d => (
                  <div key={d.id} style={card}>
                    <div style={designRow}>
                      {d.image_url && <img src={d.image_url} alt="" style={designThumb} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={lineText}>
                          {d.title ? `"${d.title}"` : "(untitled)"}
                          {d.featured && <span style={adminTag}> · Featured</span>}
                        </p>
                        <span style={metaText}>
                          by @{d.username} · {new Date(d.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div style={actionsRow}>
                      <button style={banBtn} disabled={designActioningId.has(d.id)} onClick={() => deleteDesign(d)}>
                        Delete
                      </button>
                      <button
                        style={d.featured ? dismissBtn : warnBtn}
                        disabled={designActioningId.has(d.id)}
                        onClick={() => toggleFeatured(d)}
                      >
                        {d.featured ? "Unfeature" : "Feature"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {designsHasMore && (
                <button style={dismissBtn} disabled={designsLoadingMore} onClick={() => searchDesigns(false)}>
                  {designsLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </>
          )}
        </>
      )}

      {view === "comments" && (
        <>
          <form onSubmit={handleCommentSearchSubmit} style={searchRow}>
            <input
              value={commentSearch}
              onChange={e => setCommentSearch(e.target.value)}
              placeholder="Search by comment text or username…"
              style={searchInput}
            />
            <button type="submit" style={dismissBtn} disabled={commentsLoading}>
              Search
            </button>
          </form>

          {commentsLoading ? (
            <p style={mutedText}>Loading comments…</p>
          ) : comments.length === 0 ? (
            <p style={mutedText}>No comments found.</p>
          ) : (
            <>
              <div className="admin-list">
                {comments.map(c => (
                  <div key={c.id} style={card}>
                    <p style={lineText}>@{c.username}</p>
                    <p style={lineText}>"{c.comment}"</p>
                    <span style={metaText}>{timeAgo(c.created_at)}</span>
                    <div style={actionsRow}>
                      <button style={banBtn} disabled={commentActioningId.has(c.id)} onClick={() => deleteComment(c)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {commentsHasMore && (
                <button style={dismissBtn} disabled={commentsLoadingMore} onClick={() => searchComments(false)}>
                  {commentsLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </>
          )}
        </>
      )}

      {view === "feedback" && (
        <>
          {feedbackLoading ? (
            <p style={mutedText}>Loading feedback…</p>
          ) : feedback.length === 0 ? (
            <p style={mutedText}>No feedback yet.</p>
          ) : (
            <div className="admin-list">
              {feedback.map(f => (
                <div key={f.id} style={card}>
                  <span style={metaText}>
                    {f.username ? `@${f.username}` : "Anonymous"} · {timeAgo(f.created_at)}
                  </span>
                  <p style={lineText}>{f.message}</p>
                </div>
              ))}

              {feedbackHasMore && (
                <button style={dismissBtn} disabled={feedbackLoadingMore} onClick={() => loadFeedback(false)}>
                  {feedbackLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {view === "log" && (
        <>
          {logLoading ? (
            <p style={mutedText}>Loading log…</p>
          ) : actionLog.length === 0 ? (
            <p style={mutedText}>No admin actions yet.</p>
          ) : (
            <div className="admin-list">
              {actionLog.map(a => (
                <div key={a.id} style={card}>
                  <p style={lineText}>
                    @{a.admin_username || "unknown"} {actionLabel(a.action)}
                    {a.target_username && ` @${a.target_username}`}
                  </p>
                  {a.details && ["warn_user", "broadcast_announcement", "enable_maintenance", "disable_maintenance"].includes(a.action) && (
                    <p style={reasonText}>"{a.details}"</p>
                  )}
                  <span style={metaText}>{timeAgo(a.created_at)}</span>
                </div>
              ))}

              {logHasMore && (
                <button style={dismissBtn} disabled={logLoadingMore} onClick={() => loadActionLog(false)}>
                  {logLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {view === "stats" && (
        <>
          {statsLoading || !stats ? (
            <p style={mutedText}>Loading stats…</p>
          ) : (
            <>
              <StatSection title="People">
                <StatCard
                  icon={IoPeopleOutline}
                  label="Users"
                  value={stats.total_users}
                  color="var(--accent-primary)"
                  onClick={() => {
                    setUsersBannedOnly(false);
                    setView("users");
                  }}
                />
                <StatCard
                  icon={IoPersonRemoveOutline}
                  label="Banned users"
                  value={stats.banned_users}
                  color="var(--accent-danger)"
                  onClick={() => {
                    setUsersBannedOnly(true);
                    setView("users");
                  }}
                />
              </StatSection>

              <StatSection title="Content">
                <StatCard
                  icon={IoImagesOutline}
                  label="Designs"
                  value={stats.total_designs}
                  color="var(--accent-gold)"
                  onClick={() => setView("designs")}
                />
                <StatCard
                  icon={IoChatbubbleOutline}
                  label="Comments"
                  value={stats.total_comments}
                  color="var(--accent-link)"
                  onClick={() => setView("comments")}
                />
              </StatSection>

              <StatSection title="Reports">
                <StatCard
                  icon={IoFlagOutline}
                  label="Pending"
                  value={stats.pending_reports}
                  color="var(--accent-danger)"
                  onClick={() => goToReportsFiltered("pending")}
                />
                <StatCard
                  icon={IoCheckmarkCircleOutline}
                  label="Actioned"
                  value={stats.actioned_reports}
                  color="var(--accent-success)"
                  onClick={() => goToReportsFiltered("actioned")}
                />
                <StatCard
                  icon={IoCloseCircleOutline}
                  label="Dismissed"
                  value={stats.dismissed_reports}
                  color="var(--text-muted)"
                  onClick={() => goToReportsFiltered("dismissed")}
                />
              </StatSection>
            </>
          )}
        </>
      )}

      {view === "analytics" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {GROWTH_RANGES.map(r => (
              <button
                key={r.label}
                style={r.days === growthRangeDays ? tabBtnActive : tabBtn}
                onClick={() => changeGrowthRange(r.days)}
                disabled={growthLoading}
              >
                {r.label}
              </button>
            ))}
          </div>

          {growthLoading || !growthStats ? (
            <p style={mutedText}>Loading analytics…</p>
          ) : (
            <div className="admin-chart-row">
              {[
                { title: "New users", field: "new_users" },
                { title: "New designs", field: "new_designs" },
                { title: "New comments", field: "new_comments" },
                { title: "Likes", field: "new_likes" },
                { title: "Downloads", field: "new_downloads" },
                { title: "Shares", field: "new_shares" }
              ].map(c => (
                <GrowthChart
                  key={c.field}
                  title={c.title}
                  data={growthStats}
                  field={c.field}
                  onExpand={() => setExpandedChart(c)}
                />
              ))}
            </div>
          )}

          <h3 style={sectionSubheading}>Signup funnel ({funnelRangeLabel(growthRangeDays)})</h3>
          {funnelLoading || !funnelStats ? (
            <p style={mutedText}>Loading funnel…</p>
          ) : (
            <SignupFunnel stages={funnelStats} />
          )}
        </>
      )}

      {view === "storage" && (
        <>
          {storageLoading || !storageStats ? (
            <p style={mutedText}>Loading storage…</p>
          ) : storageStats.length === 0 ? (
            <p style={mutedText}>No storage buckets found.</p>
          ) : (
            (() => {
              const grandTotal = storageStats.reduce((a, b) => a + b.total_bytes, 0) || 1;
              return (
                <>
                  <div style={cardHeader}>
                    <p style={{ ...lineText, fontWeight: 700, margin: 0 }}>Total storage</p>
                    <span style={chartTotal}>{formatBytes(grandTotal)}</span>
                  </div>
                  <div className="admin-list" style={{ marginTop: 14 }}>
                    {storageStats.map(b => {
                      const pct = (b.total_bytes / grandTotal) * 100;
                      return (
                        <div key={b.bucket_id} style={card}>
                          <div style={cardHeader}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-heading)" }}>{b.bucket_id}</span>
                            <span style={statValue}>{formatBytes(b.total_bytes)}</span>
                          </div>
                          <div style={storageBarTrack}>
                            <div style={{ ...storageBarFill, width: `${Math.max(pct, 2)}%` }} />
                          </div>
                          <p style={{ ...statLabel, margin: "8px 0 0" }}>
                            {b.object_count} files · {pct.toFixed(1)}% of total
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()
          )}
        </>
      )}

      {view === "settings" && (
        <>
          <p style={{ ...lineText, fontWeight: 700 }}>Maintenance mode</p>
          {maintenanceLoading ? (
            <p style={mutedText}>Loading…</p>
          ) : (
            <div style={card}>
              <p style={metaText}>
                {maintenanceEnabled
                  ? "The site is currently showing the maintenance page to everyone except admins."
                  : "The site is live for everyone."}
              </p>
              <textarea
                value={maintenanceMessage}
                onChange={e => setMaintenanceMessage(e.target.value)}
                placeholder="Message shown on the maintenance page (optional)…"
                style={broadcastTextarea}
                maxLength={300}
              />
              <div style={actionsRow}>
                {maintenanceEnabled ? (
                  <button style={dismissBtn} disabled={maintenanceSaving} onClick={() => saveMaintenanceMode(false)}>
                    {maintenanceSaving ? "Saving…" : "Turn off maintenance mode"}
                  </button>
                ) : (
                  <button style={banBtn} disabled={maintenanceSaving} onClick={() => saveMaintenanceMode(true)}>
                    {maintenanceSaving ? "Saving…" : "Turn on maintenance mode"}
                  </button>
                )}
              </div>
            </div>
          )}

          <p style={{ ...lineText, fontWeight: 700, marginTop: 20 }}>Send announcement to everyone</p>
          <div style={card}>
            <form onSubmit={sendBroadcast}>
              <textarea
                value={broadcastMessage}
                onChange={e => setBroadcastMessage(e.target.value)}
                placeholder="Message every user will see as a notification…"
                style={broadcastTextarea}
                maxLength={500}
              />
              <div style={actionsRow}>
                <button type="submit" style={warnBtn} disabled={broadcastSending || !broadcastMessage.trim()}>
                  {broadcastSending ? "Sending…" : "Send to everyone"}
                </button>
              </div>
              {broadcastResult && <p style={reasonText}>{broadcastResult}</p>}
            </form>
          </div>
        </>
      )}
        </div>
      </div>

      {expandedChart &&
        createPortal(
          <div style={chartModalBackdrop} onClick={() => setExpandedChart(null)}>
            <div style={chartModalSheet} ref={chartModalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={expandedChart.title} onClick={e => e.stopPropagation()}>
              <button style={chartModalClose} onClick={() => setExpandedChart(null)} aria-label="Close">
                <IoClose size={22} />
              </button>
              <GrowthChart title={expandedChart.title} data={growthStats} field={expandedChart.field} large />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// Plain inline bars — no charting library needed. Handles anywhere from a
// single day (Day range) to 365 daily bars (Year range): the gap between
// bars shrinks as the count grows (365 bars at a 4px gap would overflow the
// row entirely), and only a spaced-out subset of x-axis labels render so
// they don't collide when there are more days than pixels to show them in.
function GrowthChart({ title, data, field, onExpand, large }) {
  const values = data.map(d => Number(d[field]) || 0);
  const max = Math.max(1, ...values);
  const total = values.reduce((a, b) => a + b, 0);
  const peakIdx = values.indexOf(Math.max(...values));
  const spansMultipleMonths = data.length > 31;
  const barGap = data.length > 90 ? 0.5 : data.length > 31 ? 1.5 : 4;
  const labelStride = Math.max(1, Math.ceil(data.length / (large ? 14 : 8)));

  function formatDay(dayStr) {
    const date = new Date(dayStr);
    return spansMultipleMonths
      ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : String(date.getDate());
  }

  return (
    <div
      style={large ? {} : { ...card, marginBottom: 14, cursor: onExpand ? "pointer" : "default" }}
      onClick={onExpand}
      role={onExpand ? "button" : undefined}
    >
      <div style={cardHeader}>
        <p style={{ ...lineText, fontWeight: 700, margin: 0 }}>{title}</p>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={chartTotal}>
            {total} <span style={chartTotalLabel}>total</span>
          </span>
          {onExpand && <IoExpandOutline style={{ color: "var(--text-muted)" }} />}
        </span>
      </div>

      {total === 0 ? (
        <p style={{ ...mutedText, padding: "28px 0", textAlign: "center" }}>Nothing to show yet.</p>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={large ? { ...chartYAxis, height: 320 } : chartYAxis}>
              <span style={chartYAxisLabel}>{max}</span>
              <span style={chartYAxisLabel}>{Math.round(max / 2)}</span>
              <span style={chartYAxisLabel}>0</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={large ? { ...chartWrap, height: 320 } : chartWrap}>
                <div style={{ ...chartGridline, top: 0 }} />
                <div style={{ ...chartGridline, top: "50%" }} />
                <div style={{ ...chartGridline, top: "100%" }} />
                <div style={{ ...chartRow, gap: barGap }}>
                  {data.map((d, i) => {
                    const value = values[i];
                    const heightPct = (value / max) * 100;
                    return (
                      <div key={d.day} style={chartBarWrap} title={`${new Date(d.day).toLocaleDateString(undefined, { month: "short", day: "numeric" })}: ${value}`}>
                        <div
                          style={{
                            ...chartBar,
                            height: `${Math.max(heightPct, value > 0 ? 4 : 1.5)}%`,
                            opacity: i === peakIdx ? 1 : 0.55
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ ...chartLabelsRow, gap: barGap }}>
                {data.map((d, i) => (
                  <span key={d.day} style={large ? { ...chartLabel, fontSize: 11.5 } : chartLabel}>
                    {i % labelStride === 0 ? formatDay(d.day) : ""}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Each bar's width is relative to the first stage reached (not the max —
// signup_started should always be the biggest number, so it's the natural
// 100% baseline for "what fraction of people who started got this far").
function SignupFunnel({ stages }) {
  const baseline = Math.max(1, stages[0]?.sessions || 0);

  if (baseline === 1 && stages.every(s => s.sessions === 0)) {
    return <p style={{ ...mutedText, padding: "12px 0" }}>No signup activity in this window yet.</p>;
  }

  return (
    <div style={{ ...card, marginBottom: 14 }}>
      {stages.map((s, i) => {
        const pct = Math.round((s.sessions / baseline) * 100);
        const dropFromPrev = i > 0 && stages[i - 1].sessions > 0
          ? Math.round((1 - s.sessions / stages[i - 1].sessions) * 100)
          : null;
        return (
          <div key={s.event} style={funnelRow}>
            <div style={funnelRowHead}>
              <span style={lineText}>{s.label}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {dropFromPrev !== null && dropFromPrev > 0 && (
                  <span style={funnelDropTag}>−{dropFromPrev}%</span>
                )}
                <span style={{ ...lineText, fontWeight: 700 }}>{s.sessions}</span>
              </span>
            </div>
            <div style={funnelBarTrack}>
              <div style={{ ...funnelBarFill, width: `${Math.max(pct, s.sessions > 0 ? 2 : 0)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatSection({ title, children }) {
  return (
    <div style={statSectionWrap}>
      <p style={statSectionTitle}>{title}</p>
      <div style={statsGrid} className="admin-stats-grid">
        {children}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <div
      style={onClick ? { ...statCard, cursor: "pointer" } : statCard}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <div style={{ ...statIconWrap, color }}>
        <Icon />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={statValue}>{value ?? 0}</div>
        <div style={statLabel}>{label}</div>
      </div>
    </div>
  );
}

function timeAgo(dateString) {
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
}

/* ===== STYLES ===== */

// maxWidth/margin deliberately NOT set here — they used to be, as plain
// inline style properties, which silently won over every later attempt to
// widen this for desktop via the .admin-page class in the <style> block
// below (an inline style always beats a class rule, media query or not,
// short of !important). The base (mobile) and 900px+ (desktop) widths both
// live in that one class now instead, so there's only one place actually in
// control of this.
const page = {
  padding: "20px 16px calc(90px + env(safe-area-inset-bottom))",
  background: "var(--bg-page)",
  minHeight: "100vh"
};

const heading = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: "var(--text-heading)",
  margin: "0 0 18px",
  fontSize: 22
};

const tabRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginBottom: 18,
  borderBottom: "1px solid var(--border-soft)",
  paddingBottom: 14
};

const tabBtn = {
  display: "inline-flex",
  alignItems: "center",
  padding: "7px 14px",
  borderRadius: 20,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-secondary)",
  fontWeight: 700,
  fontSize: 12.5,
  cursor: "pointer",
  transition: "background 0.15s ease, border-color 0.15s ease"
};

const tabBtnActive = {
  ...tabBtn,
  border: "1.5px solid var(--accent-primary)",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
};

const tabBadge = {
  marginLeft: 6,
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  borderRadius: 8,
  background: "var(--accent-danger)",
  color: "#fff",
  fontSize: 10,
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1
};

const tabBadgeActive = {
  ...tabBadge,
  background: "rgba(255,255,255,0.9)",
  color: "var(--accent-primary)"
};

const filterRow = { display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" };

const filterBtn = {
  padding: "5px 12px",
  borderRadius: 14,
  border: "1px solid var(--border-input)",
  background: "none",
  color: "var(--text-secondary)",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer"
};

const filterBtnActive = {
  ...filterBtn,
  border: "1px solid var(--accent-primary)",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)"
};

const statusTag = { textTransform: "capitalize" };

const searchRow = { display: "flex", gap: 8, marginBottom: 14 };

const searchInput = {
  flex: 1,
  padding: "9px 12px",
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  fontSize: 13.5,
  outline: "none"
};

const broadcastTextarea = {
  width: "100%",
  minHeight: 70,
  padding: "9px 12px",
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-page)",
  color: "var(--text-heading)",
  fontSize: 13.5,
  outline: "none",
  resize: "vertical",
  fontFamily: "inherit",
  boxSizing: "border-box"
};

const userRow = { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 };

const userAvatar = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)",
  flexShrink: 0
};

const adminTag = { color: "var(--accent-gold)", fontWeight: 700 };
const verifiedTag = { color: "var(--accent-link)", fontWeight: 700 };

const designRow = { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 };

const designThumb = {
  width: 48,
  height: 48,
  borderRadius: 8,
  objectFit: "cover",
  border: "1px solid var(--border-medium)",
  flexShrink: 0
};

const statSectionWrap = { marginBottom: 24 };

const statSectionTitle = {
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-muted)",
  margin: "0 0 10px"
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12
};

const statCard = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 12,
  padding: "16px 18px",
  display: "flex",
  alignItems: "center",
  gap: 14
};

const statIconWrap = {
  width: 42,
  height: 42,
  borderRadius: 10,
  background: "var(--bg-page)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 20,
  flexShrink: 0
};

const statValue = { fontSize: 22, fontWeight: 800, color: "var(--text-heading)", lineHeight: 1.2 };
const statLabel = { fontSize: 12.5, color: "var(--text-muted)" };

const storageBarTrack = { height: 8, borderRadius: 4, background: "var(--bg-page)", overflow: "hidden", marginTop: 12 };
const storageBarFill = { height: "100%", background: "var(--accent-primary)", borderRadius: 4, transition: "width 0.3s" };

const chartWrap = {
  position: "relative",
  height: 130
};

const chartYAxis = {
  height: 130,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-end",
  paddingBottom: 1
};

const chartYAxisLabel = {
  fontSize: 9.5,
  color: "var(--text-muted)",
  fontVariantNumeric: "tabular-nums"
};

const chartGridline = {
  position: "absolute",
  left: 0,
  right: 0,
  borderTop: "1px dashed var(--border-soft)"
};

const chartRow = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "flex-end",
  gap: 4
};

const chartBarWrap = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
  height: "100%",
  minWidth: 0
};

const chartBar = {
  width: "70%",
  minHeight: 0,
  background: "var(--accent-primary)",
  borderRadius: "3px 3px 0 0",
  transition: "opacity 0.15s"
};

const sectionSubheading = {
  fontSize: 15.5,
  fontWeight: 700,
  color: "var(--text-heading)",
  margin: "22px 0 10px"
};

const funnelRow = { marginBottom: 14 };

const funnelRowHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 5
};

const funnelBarTrack = {
  height: 8,
  borderRadius: 4,
  background: "var(--bg-soft)",
  overflow: "hidden"
};

const funnelBarFill = {
  height: "100%",
  background: "var(--accent-primary)",
  borderRadius: 4,
  transition: "width 0.3s"
};

const funnelDropTag = {
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--accent-danger)"
};

const chartLabelsRow = { display: "flex", gap: 4, marginTop: 8 };
const chartLabel = { flex: 1, textAlign: "center", fontSize: 9.5, color: "var(--text-muted)" };
const chartTotal = { fontSize: 18, fontWeight: 800, color: "var(--text-heading)" };
const chartTotalLabel = { fontSize: 11, fontWeight: 500, color: "var(--text-muted)" };

const chartModalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(20,14,8,0.55)",
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20
};

const chartModalSheet = {
  position: "relative",
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 12,
  padding: 20,
  width: "100%",
  maxWidth: 640,
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)"
};

const chartModalClose = {
  position: "absolute",
  top: 12,
  right: 12,
  border: "none",
  background: "none",
  color: "var(--text-muted)",
  cursor: "pointer",
  display: "flex",
  padding: 4
};

const mutedText = { padding: 24, fontSize: 14, color: "var(--text-muted)" };

const card = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 8
};

const typeBadge = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--accent-primary)",
  background: "var(--bg-soft)",
  padding: "3px 10px",
  borderRadius: 20
};

const metaText = { fontSize: 12, color: "var(--text-muted)" };

const lineText = {
  margin: "4px 0",
  fontSize: 13.5,
  color: "var(--text-secondary)"
};

const bannedTag = { color: "var(--accent-danger)", fontWeight: 700 };

const reasonText = {
  margin: "8px 0 0",
  fontSize: 13,
  color: "var(--text-body)",
  fontStyle: "italic"
};

const actionsRow = {
  display: "flex",
  gap: 8,
  marginTop: 14
};

const dismissBtn = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "1.5px solid var(--border-input)",
  background: "none",
  color: "var(--text-secondary)",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer"
};

const warnBtn = {
  ...dismissBtn,
  border: "1.5px solid var(--accent-gold)",
  color: "var(--accent-gold)"
};

const banBtn = {
  ...dismissBtn,
  border: "none",
  background: "var(--accent-danger)",
  color: "var(--accent-contrast)"
};

const verifyBtn = {
  ...dismissBtn,
  border: "1.5px solid var(--accent-link)",
  color: "var(--accent-link)"
};
